/**
 * i18n-jsautotranslate 初始化
 * 两行 js 实现整页自动翻译，无需语言配置文件、无 API Key
 * 文档：https://translate.zvo.cn
 */
import translate from 'i18n-jsautotranslate';
import { I18N_COPY } from './i18nCopy';
import { configureBrandTranslateIgnore } from './brand';
import {
  isLangCategoryRouting,
  parseLangCategoryPath,
  languageCodeToId,
} from './routes';
const warnedMissingKeys = new Set();

// 根据浏览器/系统语言推断默认语言（与 LANGUAGE_OPTIONS 中的 id 对应）
function detectDefaultLanguageId() {
  if (typeof window === 'undefined') return 'english';
  const navLang =
    (window.navigator.language || window.navigator.userLanguage || 'en').toLowerCase();

  if (navLang === 'zh-tw' || navLang === 'zh-hk' || navLang === 'zh-mo' || navLang === 'zh-hant') {
    return 'chinese_traditional';
  }
  if (navLang.startsWith('zh')) return 'chinese_traditional';
  if (navLang.startsWith('ru')) return 'russian';

  return 'english';
}

export function initI18n() {
  if (typeof window === 'undefined') return;
  try {
    configureBrandTranslateIgnore(translate);

    // 语言选择框，点击 Language 弹出我们自己的选择菜单
    if (translate.selectLanguageTag) translate.selectLanguageTag.show = false;

    // 设置页面原始语种为英文
    translate.language.setLocal('english');

    // 使用客户端翻译通道（无需自己的服务器）
    translate.service.use('client.edge');

    // 开启对动态渲染内容的监控（React 等框架必须）
    translate.listener.start();

    // 先执行一次翻译初始化
    translate.execute();

    // 目标语言优先级：
    // 1) 生产环境 URL 中的语言段（/en/game 等）
    // 2) 用户手动选择并持久化在 localStorage.language
    // 3) 系统语言推断默认值
    let targetLangId = null;
    if (isLangCategoryRouting()) {
      const parsed = parseLangCategoryPath(window.location.pathname);
      if (parsed?.lang) {
        targetLangId = languageCodeToId(parsed.lang);
        try {
          window.localStorage.setItem('language', targetLangId);
        } catch {
          // ignore
        }
      }
    }
    if (
      window.localStorage.getItem('language') === 'chinese_simplified'
    ) {
      try {
        window.localStorage.setItem('language', 'chinese_traditional');
      } catch {
        // ignore
      }
    }
    if (!targetLangId) {
      targetLangId =
        normalizeLanguageId(window.localStorage.getItem('language')) ||
        detectDefaultLanguageId();
    }
    if (typeof translate.changeLanguage === 'function') {
      // 如果当前语言与目标语言不同，则切换
      const current =
        typeof translate.currentLanguage === 'function'
          ? translate.currentLanguage()
          : null;
      if (!current || current !== targetLangId) {
        translate.changeLanguage(targetLangId);
      }
    }
  } catch (e) {
    console.warn('i18n-jsautotranslate init:', e);
  }
}

/** 常用语种列表，供侧边栏 Language 下拉使用（名称固定为各语言原生写法，不参与自动翻译） */
export const LANGUAGE_OPTIONS = [
  { id: 'english', name: 'English' },
  { id: 'russian', name: 'Русский' },
  { id: 'chinese_traditional', name: '繁體中文' },
];

function normalizeLanguageId(langId) {
  if (langId === 'chinese_simplified') return 'chinese_traditional';
  return langId;
}

export function getCurrentLanguageId() {
  if (typeof window === 'undefined') return 'english';
  try {
    const saved = window.localStorage.getItem('language');
    if (saved) return normalizeLanguageId(saved);
  } catch (e) {
    // ignore storage errors
  }
  return detectDefaultLanguageId();
}

function formatTemplate(template, params = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : `{${key}}`,
  );
}

/**
 * 手动文案配置优先；未配置时回退 fallback（由自动翻译继续处理）
 */
export function t(key, fallback, params = {}, langId) {
  const currentLang = langId || getCurrentLanguageId();
  const languagePack = I18N_COPY[currentLang] || {};
  const englishPack = I18N_COPY.english || {};
  const hasCurrent = Object.prototype.hasOwnProperty.call(languagePack, key);
  const hasEnglish = Object.prototype.hasOwnProperty.call(englishPack, key);
  const template = hasCurrent
    ? languagePack[key]
    : hasEnglish
      ? englishPack[key]
      : fallback;

  if (!hasCurrent && !hasEnglish) {
    const signature = `${currentLang}:${key}`;
    if (
      process.env.NODE_ENV !== 'production' &&
      !warnedMissingKeys.has(signature)
    ) {
      warnedMissingKeys.add(signature);
      console.warn(`[i18n] Missing copy key "${key}" for "${currentLang}"`);
    }
  }
  return formatTemplate(template, params);
}

/**
 * 用户昵称 / 用户名由用户自定义，不应被整页自动翻译改写。
 * 用于 i18n-jsautotranslate（见 translate.listener）及浏览器翻译。
 */
export const noAutoTranslateUserTextProps = {
  translate: "no",
  "data-i18n-ignore": "true",
  "data-i18n": "no",
};

/**
 * 用户昵称等：同时加 notranslate 与（可选）class「ignore」。
 * i18n-jsautotranslate 默认只识别 ignore，不认 HTML notranslate。
 *
 * @param {boolean} useTranslateJsIgnore 为 true 时追加 ignore
 * @param {...string} classNames 其余样式 class
 */
export function mergeReaderNameClass(useTranslateJsIgnore, ...classNames) {
  return [...classNames.filter(Boolean), "notranslate", useTranslateJsIgnore ? "ignore" : null]
    .filter(Boolean)
    .join(" ");
}

export { translate };
